import { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { usePermission } from '@apigrowfinance/pbolt-permission-provider';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useAccount } from '@contexts/AccountProvider';
import AccountStatusEnum from '@enums/account/status';
import accountStatusUserFriendlyTranslations from '@messages/accountStatusUserFriendlyTranslations';
import programTypes from '@messages/programTypes';
import AccountChangeStatus from '@pages/accountContainer/AccountStatus/AccountChangeStatus';
import ProgramStatus from '@pages/accountContainer/statusBar/ProgramStatus';
import StatusBarContainer from '@pages/accountContainer/statusBar/statusBarContainer/StatusBarContainer';
import StatusBarItem from '@pages/accountContainer/statusBar/statusBarItem/StatusBarItem';
import getEnumTranslation from '@utils/getEnumTranslation';

const AccountStatus = () => {
    const [searchParams] = useSearchParams();
    const initialModalOpen = searchParams.get('changeStatus') === 'true';
    const { theme } = useTheme();
    const { formatMessage } = useIntl();
    const { account, isLoading: isLoadingAccount, refetch } = useAccount();
    const [changeStatusOpen, setChangeStatusOpen] = useState(initialModalOpen);
    const [isFocused, setIsFocused] = useState(false);
    const { isAllowedTo } = usePermission();

    useEffect(() => {
        if (searchParams.get('changeStatus') === 'true') {
            setChangeStatusOpen(true);
        }
    }, [searchParams]);

    const accountStatusAriaLabel = formatMessage({
        id: 'AccountStatus.ariaLabelChangeAccountStatus',
        defaultMessage: 'Change account status',
    });

    const isAllowedToChangeStatus = isAllowedTo('edit.accountStatus');

    const handleClick = () => {
        if (!isAllowedToChangeStatus) {
            return;
        }
        setChangeStatusOpen(true);
    };

    const closeChangeStatus = () => {
        refetch.accountRefetch();
        setChangeStatusOpen(false);
    };

    const activeStatus = isLoadingAccount ? null : account?.status;
    const programStatus = ProgramStatus();
    return (
        <>
            <StatusBarContainer focused={isFocused}>
                {programStatus}
                <StatusBarItem
                    isLoading={isLoadingAccount}
                    tooltipText={
                        <FormattedMessage
                            id="AccountStatus.accountProgram"
                            defaultMessage="Account program"
                        />
                    }>
                    <Typography variant="overline">
                        {getEnumTranslation(account?.program_type, {
                            messages: programTypes,
                        })}
                    </Typography>
                </StatusBarItem>
                <StatusBarItem
                    tooltipText={
                        <FormattedMessage
                            id="AccountStatus.accountStatusTitle"
                            defaultMessage="Account status"
                        />
                    }
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    ariaLabel={accountStatusAriaLabel}
                    isLoading={isLoadingAccount}
                    {...(isAllowedToChangeStatus
                        ? { shortcut: 'Shift+s' }
                        : {})}
                    onClick={handleClick}>
                    <Box sx={{ mr: 1, display: 'flex' }}>
                        <svg height="6" width="6">
                            <circle
                                cx="3"
                                cy="3"
                                r="3"
                                fill={
                                    activeStatus === AccountStatusEnum.NORMAL
                                        ? theme.palette.success.main
                                        : theme.palette.error.main
                                }
                            />
                        </svg>
                    </Box>
                    <Typography variant="overline" marginRight={0.5}>
                        {getEnumTranslation(activeStatus, {
                            messages: accountStatusUserFriendlyTranslations,
                        })}
                    </Typography>
                    {changeStatusOpen ? (
                        <RiArrowUpSLine />
                    ) : (
                        <RiArrowDownSLine />
                    )}
                </StatusBarItem>
            </StatusBarContainer>
            {changeStatusOpen && (
                <AccountChangeStatus
                    activeStatus={activeStatus}
                    closeChangeStatus={closeChangeStatus}
                />
            )}
        </>
    );
};

export default AccountStatus;
